import './Order.css'

import React from "react"
import { isEmptySortValue } from './sortValue'

export interface Order {
  key: string
  asc: boolean
}

export type CompareFunction<T> = (a: T, b: T, asc: boolean) => number


export class OrderManager {
  private allowedElements: string[]
  private order: Order
  private setter: (order: Order) => void
  private compareFunctions?: Record<string, CompareFunction<any>>

  constructor(
    allowedElements: string[],
    order: Order,
    setter: (order: Order) => void,
    compareFunctions?: Record<string, CompareFunction<any>>
  ) {
    this.allowedElements = allowedElements
    this.order = order
    this.setter = setter
    this.compareFunctions = compareFunctions

    if (!allowedElements.includes(this.order.key)) {
      throw new Error(`Default key "${this.order.key}" is not in allowed elements`)
    }
  }

  // Set current key only if allowed
  setCurrentOrderKey(key: string) {
    if (!this.allowedElements.includes(key)) {
      throw new Error(`Key "${key}" is not allowed`)
    }
    this.order.key = key
    this.setter(this.order)
  }

  setDirection(direction: boolean) {
    this.order.asc = direction
    this.setter(this.order)
  }

  click(key: string) {
    if (!this.allowedElements.includes(key)) {
      throw new Error(`Key "${key}" is not allowed`)
    }
    if (this.order.key == key) {
      this.order.asc = !this.order.asc
      this.setter(this.order)
      return
    }
    this.order.key = key
    this.setter(this.order)
  }

  // Helper to get nested property by dot notation
  private getValue(obj: any, path: string): any {
    return path.split('.').reduce((o, p) => (o ? o[p] : undefined), obj)
  }

  // Sorts an array of objects in-place by the current key and direction
  orderListByKey<T>(list: T[]): T[] {
    const key = this.order.key
    const compareFn = this.compareFunctions?.[key]
    const asc = this.order.asc

    const comparator = (a: T, b: T) => {
      const aValue = this.getValue(a, key)
      const bValue = this.getValue(b, key)

      const aEmpty = isEmptySortValue(aValue), bEmpty = isEmptySortValue(bValue)
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1

      return compareFn
        ? compareFn(a, b, asc)
        : this.#defaultCompare(aValue, bValue, asc)
    }

    return [...list].sort(comparator)
  }

  #defaultCompare(aValue: any, bValue: any, asc: boolean): number {
    const dir = asc ? 1 : -1
    if (aValue === bValue) return 0
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.toLowerCase() > bValue.toLowerCase() ? dir : -dir
    }
    return aValue > bValue ? dir : -dir
  }

  orderInfoElement(key: string): React.ReactElement | null {
    if (key !== this.order.key) {
      return null
    }
    return <div className={`order-info ${this.order.asc ? 'order-info-asc' : 'order-info-desc'}`}>
      <i className='svgfont-arrow-up' />
    </div>
  }

  newManager(order: Order): OrderManager {
    return new OrderManager(this.allowedElements, order, this.setter, this.compareFunctions)
  }

  urlParams(): string {
    return `order.direction=${this.order.asc ? 'ASC' : 'DESC'}&order.column=${this.order.key}`
  }
}
